import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Segmented } from "../ui";
import {
  getUserModuleReport,
  getUserModuleVideoReport,
} from "../../services/UserReportService";
import { getAnswers } from "../../services/AssessmentService";

/**
 * The learner's own analytics, as KPI tiles.
 *
 * A learner opens this to answer "how am I doing", which is a handful of
 * headline numbers rather than a per-module comparison. Each card is therefore
 * a row of stat tiles — the number is the chart. Values stay in ink; nothing
 * here uses colour to carry meaning.
 *
 * The data contracts are untouched: the same endpoints, the same range params,
 * the same fields.
 */

/* ----------------------------------------------------------------- ranges */

const RANGES = [
  { value: "This Month", label: "1M" },
  { value: "This Week", label: "1W" },
  { value: "Today", label: "1D" },
  { value: "Custom", label: "Custom" },
];

/** `series` values arrive as whole minutes, so 0 means "under a minute". */
const formatMinutes = (minutes) => {
  const total = Math.round(Number(minutes) || 0);
  if (total <= 0) return "<1m";
  if (total < 60) return `${total}m`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

/** The API nests each category name in a single-element array. */
const categoryName = (c) => (Array.isArray(c) ? c[0] : c);

const formatPercent = (v) => `${Math.round(v)}%`;

/* ------------------------------------------------------------ primitives */

/**
 * One headline number. `caption` qualifies the value (which module, out of
 * how many) so the tile reads on its own without a chart behind it. `text`
 * sets a name rather than a figure at a size where the name stays legible.
 */
const Kpi = ({ label, value, caption, text }) => (
  <div className="ds-kpi">
    <div className="ds-kpi__label">{label}</div>
    <div
      className={`ds-kpi__value${text ? " ds-kpi__value--text" : ""} ds-truncate`}
      title={value}
    >
      {value}
    </div>
    {caption && (
      <div className="ds-kpi__caption ds-truncate" title={caption}>
        {caption}
      </div>
    )}
  </div>
);

/** Loading and empty states keep the tiles in place so the card doesn't jump. */
const KpiRow = ({ tiles, loading, hint }) => (
  <div className="ds-kpis-wrap">
    <div className="ds-kpis" aria-busy={loading || undefined}>
      {tiles.map((t) => (
        <Kpi
          key={t.label}
          label={t.label}
          value={loading ? "…" : t.value}
          caption={loading ? null : t.caption}
          text={t.text && !loading}
        />
      ))}
    </div>
    {!loading && hint}
  </div>
);

const Hint = ({ text, action }) => (
  <div className="ds-kpis__hint">
    <span>{text}</span>
    {action}
  </div>
);

const CardHead = ({ title, range, onRange, startDate, endDate, onStart, onEnd }) => (
  <>
    <div className="ds-card__head">
      <h3 className="ds-h3">{title}</h3>
      <Segmented options={RANGES} value={range} onChange={onRange} />
    </div>
    {range === "Custom" && (
      <div
        className="ds-row-flex ds-gap-2"
        style={{ padding: "var(--s-3) var(--s-5) 0" }}
      >
        <input
          type="date"
          className="ds-date"
          value={startDate}
          onChange={(e) => onStart(e.target.value)}
        />
        <span className="ds-muted" style={{ fontSize: 12 }}>
          to
        </span>
        <input
          type="date"
          className="ds-date"
          value={endDate}
          onChange={(e) => onEnd(e.target.value)}
        />
      </div>
    )}
  </>
);

/* ------------------------------------------------- time-per-module cards */

/**
 * Minutes spent per module, for either decks or video. One component because
 * the only differences between the two are the endpoint and the title.
 */
const TimeAnalysisCard = ({ title, fetcher, overallKey }) => {
  const navigate = useNavigate();
  const [range, setRange] = useState("This Month");
  const [startDate, setStartDate] = useState("Start");
  const [endDate, setEndDate] = useState("End");
  const [rows, setRows] = useState([]);
  const [overall, setOverall] = useState(null);
  const [loading, setLoading] = useState(true);

  // "Custom" is meaningless without dates; seed a sensible yesterday→today.
  useEffect(() => {
    if (range === "Custom") {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      setStartDate(yesterday.toISOString().split("T")[0]);
      setEndDate(today.toISOString().split("T")[0]);
    }
  }, [range]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await fetcher(range, startDate, endDate);
        if (cancelled || !result) return;
        const { categories = [], series = [] } = result;
        setRows(
          categories.map((c, i) => ({
            label: categoryName(c),
            value: Number(series[i]) || 0,
          }))
        );
        setOverall(result[overallKey] || null);
      } catch (e) {
        if (e?.message === "Unauthorized" || e?.message === "Token Expired") {
          localStorage.removeItem("token");
          navigate("/");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range, startDate, endDate, fetcher, overallKey, navigate]);

  const tiles = useMemo(() => {
    const opened = rows.filter((r) => r.value > 0);
    const minutes = opened.reduce((sum, r) => sum + r.value, 0);
    const top = [...opened].sort((a, b) => b.value - a.value)[0];
    const topName = overall?.mostUsedModule?.module || top?.label;
    const topRow = rows.find((r) => r.label === topName) || top;
    return [
      {
        label: "Total time",
        value: overall?.totalTimeOfAllModules || formatMinutes(minutes),
      },
      {
        label: "Top module",
        value: topName || "—",
        caption: topRow ? formatMinutes(topRow.value) : null,
        text: Boolean(topName),
      },
      {
        label: "Modules opened",
        value: String(opened.length),
        caption: rows.length > opened.length ? `of ${rows.length}` : null,
      },
      {
        label: "Avg per module",
        value: opened.length ? formatMinutes(minutes / opened.length) : "—",
      },
    ];
  }, [rows, overall]);

  const empty = !rows.some((r) => r.value > 0);

  return (
    <Card>
      <CardHead
        title={title}
        range={range}
        onRange={setRange}
        startDate={startDate}
        endDate={endDate}
        onStart={setStartDate}
        onEnd={setEndDate}
      />
      <div className="ds-card__body">
        <KpiRow
          tiles={tiles}
          loading={loading}
          hint={empty && <Hint text="No activity recorded for this period yet." />}
        />
      </div>
    </Card>
  );
};

/* -------------------------------------------------------- score-per-item */

/**
 * Best score per attempted item. `module === null` distinguishes a standalone
 * warm up from a module assessment — that's the server's own distinction, kept
 * here so both cards read from the single `getAnswer` payload.
 */
const ScoreAnalysisCard = ({ title, kind, emptyText, emptyAction }) => {
  const [rows, setRows] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const response = await getAnswers();
      const scores = response?.data?.myScore || [];
      const best = new Map();
      const mine = scores.filter((s) =>
        kind === "warmup"
          ? s.assessment?.module == null
          : s.assessment?.module != null
      );
      mine.forEach((s) => {
        const name = s.assessment?.name;
        if (!name) return;
        const pct = Number(s.percentage) || 0;
        if (!best.has(name) || best.get(name) < pct) best.set(name, pct);
      });
      setRows(
        [...best.entries()]
          .map(([label, value]) => ({ label, value }))
          .sort((a, b) => b.value - a.value)
      );
      setAttempts(mine.filter((s) => s.assessment?.name).length);
      setLoading(false);
    })();
  }, [kind]);

  // Averages run over each item's best attempt, so a retake that improved a
  // score isn't dragged down by the first try.
  const tiles = useMemo(() => {
    const best = rows[0];
    const worst = rows.length > 1 ? rows[rows.length - 1] : null;
    const avg = rows.length
      ? rows.reduce((sum, r) => sum + r.value, 0) / rows.length
      : null;
    return [
      {
        label: "Attempted",
        value: String(rows.length),
        caption:
          attempts > rows.length ? `${attempts} attempts in total` : null,
      },
      {
        label: "Average score",
        value: avg == null ? "—" : formatPercent(avg),
        caption: rows.length ? "best attempt per item" : null,
      },
      {
        label: "Best score",
        value: best ? formatPercent(best.value) : "—",
        caption: best?.label,
      },
      {
        label: "Needs revisiting",
        value: worst ? formatPercent(worst.value) : "—",
        caption: worst?.label,
      },
    ];
  }, [rows, attempts]);

  return (
    <Card>
      <div className="ds-card__head">
        <h3 className="ds-h3">{title}</h3>
      </div>
      <div className="ds-card__body">
        <KpiRow
          tiles={tiles}
          loading={loading}
          hint={!rows.length && <Hint text={emptyText} action={emptyAction} />}
        />
      </div>
    </Card>
  );
};

/* ------------------------------------------------------------- the section */

export const AnalyticsSection = ({ onGoToWarmUp }) => (
  <section className="ds-section">
    <div className="ds-section__head">
      <div>
        <h2 className="ds-h2">Your activity</h2>
        <p className="ds-sub">
          Time spent and scores achieved, per module.
        </p>
      </div>
    </div>

    <div className="ds-grid ds-grid--2">
      <TimeAnalysisCard
        title="Module wise PPT analysis"
        fetcher={getUserModuleReport}
        overallKey="pptOverall"
      />
      <TimeAnalysisCard
        title="Module wise video analysis"
        fetcher={getUserModuleVideoReport}
        overallKey="videoOverall"
      />
      <ScoreAnalysisCard
        title="Attended warm up analysis"
        kind="warmup"
        emptyText="No warm ups attended yet. They're the quickest way to check what's stuck."
        emptyAction={
          onGoToWarmUp && (
            <button type="button" className="ds-btn ds-btn--outline ds-btn--sm" onClick={onGoToWarmUp}>
              Go to warm up
            </button>
          )
        }
      />
      <ScoreAnalysisCard
        title="Attended assessment analysis"
        kind="assessment"
        emptyText="Attempt an assessment and your scores will appear here."
      />
    </div>
  </section>
);

export default AnalyticsSection;
